import React from 'react'
import styled from 'styled-components'

import {
  BaseResponsiveDropdownProps,
  Button,
  Dropdown,
  Tooltip,
  useResponsiveDropdown,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'
import {
  NotificationListInfoFragment,
  useSetNotificationSubscriptionMutation,
  useUpdateNotificationMutation,
} from '@sketch/gql-types'

import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-circle-17'
import { ReactComponent as MarkReadIcon } from '@sketch/icons/circle-checkmark-17'
import { ReactComponent as MarkUnreadIcon } from '@sketch/icons/img-mail-envelope-dot-17'
import { ReactComponent as EllipsisIcon } from '@sketch/icons/ellipsis-17'
import { ReactComponent as PinIcon } from '@sketch/icons/pushpin-17'
import { ReactComponent as FollowIcon } from '@sketch/icons/misc-notification-17'
import { ReactComponent as UnfollowIcon } from '@sketch/icons/misc-notification-slash-17'

import { useSetNotificationState } from '../InboxPreview/useSetNotificationState'
import { recordPinned, recordUnpinned } from '../../views/Inbox/pinOrder'

/** Download URL for an asset-ready notification, when the asset is downloadable. */
const getDownloadableAssetPath = (
  notification: NotificationListInfoFragment
): string | null => {
  if (notification.__typename !== 'DownloadableAssetRenderedNotification') {
    return null
  }
  const asset = notification.notifiedVersion?.document?.downloadableAssets[0]
  if (!asset || asset.status !== 'AVAILABLE' || !asset.path) return null
  return asset.path
}

// Icon-only button: drop the horizontal padding so it stays square (the size
// sets the height). The icons have no intrinsic dimensions, so without an
// explicit size they collapse to 0×0 inside the flex layout — pin a fixed size
// and stop them from shrinking.
const ActionButton = styled(Button)`
  padding: 0;
  aspect-ratio: 1;
  color: ${({ theme }) => theme.colors.foreground.secondary.C};

  & + & {
    margin: 0;
  }

  svg {
    flex-shrink: 0;
    width: 17px;
    height: 17px;
  }
`

interface HoverActionProps {
  label: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  onClick: () => void
}

const HoverAction = ({ label, icon: Icon, onClick }: HoverActionProps) => (
  <Tooltip placement="top" content={label} disableWhenTouchDevice>
    {props => (
      <ActionButton
        type="button"
        size="24"
        variant="tertiary"
        aria-label={label}
        onClick={onClick}
        {...props}
      >
        <Icon />
      </ActionButton>
    )}
  </Tooltip>
)

/** Always-present read-state toggle. */
const MarkReadAction = ({
  notification,
}: {
  notification: NotificationListInfoFragment
}) => {
  const setNotificationState = useSetNotificationState()
  const isUnread = notification.state === 'UNREAD'

  return (
    <HoverAction
      label={isUnread ? 'Mark as read' : 'Mark as unread'}
      icon={isUnread ? MarkReadIcon : MarkUnreadIcon}
      onClick={() =>
        setNotificationState(
          notification.identifier,
          notification.__typename,
          isUnread ? 'READ' : 'UNREAD'
        )
      }
    />
  )
}

/**
 * The notification-type-specific primary action (the leftmost button). Today
 * that's download for a ready asset; other types have no primary action yet.
 */
const DynamicAction = ({
  notification,
}: {
  notification: NotificationListInfoFragment
}) => {
  const setNotificationState = useSetNotificationState()

  const assetPath = getDownloadableAssetPath(notification)
  if (assetPath) {
    return (
      <HoverAction
        label="Download"
        icon={DownloadIcon}
        onClick={() => {
          window.open(assetPath, '_self')
          setNotificationState(
            notification.identifier,
            notification.__typename,
            'READ'
          )
        }}
      />
    )
  }

  return null
}

interface MoreMenuDropdownProps extends BaseResponsiveDropdownProps {
  notification: NotificationListInfoFragment
}

/** The overflow menu: pin/unpin plus any type-specific secondary actions. */
const MoreMenuDropdown = ({ notification, hide }: MoreMenuDropdownProps) => {
  const { showToast } = useToast()
  const { identifier, isPinned } = notification

  const [updateNotification] = useUpdateNotificationMutation({
    onError: 'show-toast',
    redirectErrors: true,
    onCompleted: data => {
      const nowPinned = data.updateNotification?.notification?.isPinned
      if (nowPinned) recordPinned(identifier)
      else recordUnpinned(identifier)
      showToast(nowPinned ? 'Pinned' : 'Unpinned')
    },
  })

  const [setSubscription] = useSetNotificationSubscriptionMutation({
    onError: 'show-toast',
    onCompleted: data =>
      showToast(
        data.setNotificationSubscription.share?.subscriptionStatus === 'ON'
          ? 'Following'
          : 'Unfollowed'
      ),
  })

  // Follow/unfollow only applies to thread notifications, which carry an
  // annotation to subscribe to. Read the fields under a discriminant check so
  // the union narrows.
  const isThread =
    notification.__typename === 'CommentNotification' ||
    notification.__typename === 'MentionNotification'

  const annotationIdentifier = isThread
    ? notification.comment?.annotation.identifier
    : undefined

  const isSubscribed =
    isThread && notification.comment?.annotation?.subscriptionStatus === 'ON'

  return (
    <>
      <Dropdown.Item
        icon={<PinIcon />}
        onClick={() => {
          updateNotification({
            variables: { identifier, input: { pin: !isPinned } },
          })
          hide()
        }}
      >
        {isPinned ? 'Unpin' : 'Pin'}
      </Dropdown.Item>

      {annotationIdentifier && (
        <Dropdown.Item
          icon={isSubscribed ? <UnfollowIcon /> : <FollowIcon />}
          onClick={() => {
            setSubscription({
              variables: {
                state: isSubscribed ? 'OFF' : 'ON',
                annotationIdentifier,
              },
            })
            hide()
          }}
        >
          {isSubscribed ? 'Unfollow thread' : 'Follow thread'}
        </Dropdown.Item>
      )}
    </>
  )
}

const MoreMenu = ({
  notification,
}: {
  notification: NotificationListInfoFragment
}) => {
  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: MoreMenuDropdown,
    dropdownProps: { notification },
    placement: 'bottom-end',
    usePortal: true,
  })

  return (
    <>
      <ActionButton
        type="button"
        size="24"
        variant="tertiary"
        aria-label="More actions"
        {...buttonProps}
      >
        <EllipsisIcon />
      </ActionButton>
      {content}
    </>
  )
}

/**
 * The actions revealed when hovering an inbox list item: a type-specific
 * primary action, an always-present read-state toggle, and an overflow menu.
 */
export const InboxListItemActions = ({
  notification,
}: {
  notification: NotificationListInfoFragment
}) => (
  <>
    <DynamicAction notification={notification} />
    <MarkReadAction notification={notification} />
    <MoreMenu notification={notification} />
  </>
)
